'use client';

import { withEfTid } from '@pickleballinc/lib/everflow/url';
import { Button } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';

import { type EmptyMascotSize } from '@/modules/players/v3/components/shared/dashboard-widget-card/dashboard-empty-mascot-layer';
import {
  emptyStateIconImageHover,
  emptyStateMascotImageHover,
  MASCOT_SLOT,
} from '@/modules/players/v3/constants/constants';
import { useWebviewNavigation } from '@/modules/players/v3/hooks/use-webview-navigation';
import type {
  PlayerDashboardEmptyCTA,
  PlayerDashboardEmptyVisual,
} from '@/modules/players/v3/types/dashboard';
import { EVERFLOW_DOMAINS } from '@/utils/helpers/constants';

export type { PlayerDashboardEmptyCTA, PlayerDashboardEmptyVisual };

export type PlayerDashboardEmptySecondaryVisual = {
  src: string;
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  width?: number;
  height?: number;
  /** Decorations default to desktop-only; set false to also show on mobile (e.g. the small ball). */
  hideOnMobile?: boolean;
};

export type PlayerDashboardEmptyContentProps = {
  title: string;
  description: string;
  visual: PlayerDashboardEmptyVisual;
  cta?: PlayerDashboardEmptyCTA | PlayerDashboardEmptyCTA[];
  className?: string;
  mascotInCardShell?: boolean;
  mascotSize?: EmptyMascotSize;
  mascotSlotClassName?: string;
  secondaryVisual?: PlayerDashboardEmptySecondaryVisual;
};

export const PAGE_EMPTY_MASCOT_SLOT = 'h-[96px] w-[80px] lg:h-[150px] lg:w-[124px]';

const SECONDARY_VISUAL_POSITION: Record<PlayerDashboardEmptySecondaryVisual['position'], string> = {
  'bottom-right': 'bottom-0 right-2',
  'bottom-left': 'bottom-0 left-2',
  'top-right': 'top-2 right-4',
  'top-left': 'top-2 left-4',
};

export const PlayerDashboardEmptyContent = ({
  title,
  description,
  visual,
  cta,
  className,
  mascotInCardShell = false,
  mascotSize = 'default',
  mascotSlotClassName,
  secondaryVisual,
}: PlayerDashboardEmptyContentProps) => {
  const router = useRouter();
  const { navigateToNativeScreenIfSupported } = useWebviewNavigation();
  const isMascot = visual.kind === 'mascot';
  const isNone = visual.kind === 'none';
  const ctas = cta ? (Array.isArray(cta) ? cta : [cta]) : [];

  const mainBlock = (
    <div
      className={clsx(
        'flex flex-col items-center text-center w-full z-[1]',
        { 'gap-4 px-4 pb-0': isMascot, 'gap-2 px-4': isNone, 'gap-3': !isMascot && !isNone },
        { 'min-h-0 flex-1 justify-center': isMascot }
      )}
    >
      {visual.kind === 'custom' ? (
        <div className='shrink-0 flex items-center justify-center'>{visual.content}</div>
      ) : null}

      {visual.kind === 'icon' ? (
        <div className={clsx('shrink-0 flex items-center justify-center', visual.className)}>
          {'component' in visual && visual.component ? (
            <visual.component
              width={visual.width ?? 64}
              height={visual.height ?? 56}
              className={clsx('object-contain w-auto', emptyStateIconImageHover)}
            />
          ) : 'src' in visual && visual.src ? (
            <img
              src={visual.src}
              alt=''
              width={visual.width ?? 64}
              height={visual.height ?? 56}
              className={clsx('object-contain w-auto', emptyStateIconImageHover)}
            />
          ) : null}
        </div>
      ) : null}

      <div
        className={clsx('flex flex-col items-center', {
          'gap-2': isMascot || isNone,
          'gap-3': !isMascot && !isNone,
        })}
      >
        <h4 className='text-base font-semibold text-gray-900 w-full'>{title}</h4>
        <p
          className={clsx(
            'text-sm text-gray-700 max-w-[263px] w-full',
            isMascot && 'text-gray-600'
          )}
        >
          {description}
        </p>
        {ctas.length ? (
          <div className='pt-1 flex flex-wrap items-center justify-center gap-2'>
            {ctas.map((item) => {
              const variant = item.variant ?? 'primary';
              return (
                <Button
                  key={item.href ?? 'action'}
                  variant={variant}
                  size={item.size ?? 'sm'}
                  disabled={item.disabled}
                  className={clsx('rounded-md', {
                    'border border-gray-300': variant === 'secondary',
                  })}
                  onClick={() => {
                    if (item.screenId && navigateToNativeScreenIfSupported(item.screenId)) {
                      return;
                    }
                    if (item.onWebviewPress) {
                      item.onWebviewPress();
                      return;
                    }
                    if (item.onClick) {
                      item.onClick();
                      return;
                    }
                    if (!item.href) return;
                    if (item.isOutsideLink) {
                      window.open(
                        withEfTid(item.href, EVERFLOW_DOMAINS),
                        '_blank',
                        'noopener,noreferrer'
                      );
                    } else {
                      router.push(withEfTid(item.href, EVERFLOW_DOMAINS));
                    }
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );

  const secondaryLayer = secondaryVisual ? (
    <div
      className={clsx(
        'pointer-events-none absolute z-0',
        secondaryVisual.hideOnMobile === false ? 'block' : 'hidden lg:block',
        SECONDARY_VISUAL_POSITION[secondaryVisual.position]
      )}
      aria-hidden
    >
      <img
        src={secondaryVisual.src}
        alt=''
        className='select-none object-contain'
        style={{ width: secondaryVisual.width ?? 120, height: secondaryVisual.height ?? 96 }}
      />
    </div>
  ) : null;

  if (isNone) {
    return (
      <div
        className={clsx(
          'flex h-full min-h-0 w-full flex-1 items-center justify-center px-4 py-6',
          className
        )}
      >
        {mainBlock}
      </div>
    );
  }

  if (visual.kind === 'mascot') {
    return (
      <div
        className={clsx(
          'relative flex h-full w-full min-h-0 flex-1 flex-col overflow-hidden',
          mascotInCardShell ? 'z-[1] justify-center' : 'pt-4',
          className
        )}
      >
        {mainBlock}
        {secondaryLayer}
        {!mascotInCardShell && (
          <div
            className={clsx(
              'pointer-events-none absolute z-0 bottom-[-2px]',
              mascotSlotClassName ?? MASCOT_SLOT[mascotSize],
              visual.position === 'bottom-left' ? 'left-[-4px]' : 'right-[-4px]'
            )}
            aria-hidden
          >
            <img
              src={visual.src}
              alt=''
              width={visual.width ?? 120}
              height={visual.height ?? 150}
              className={clsx(
                'h-full w-full min-h-0 min-w-0 object-contain object-bottom select-none',
                visual.position === 'bottom-left' ? 'object-left' : 'object-right',
                emptyStateMascotImageHover(visual.position),
                visual.className
              )}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'flex h-full min-h-0 w-full flex-1 items-center justify-center py-2',
        className
      )}
    >
      {mainBlock}
    </div>
  );
};
